import { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Check,
  CheckCircle2,
  ChevronsUpDown,
  Loader2,
  PlugZap,
  Plus,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import type { OrgStructureResponse } from "@/api/configuration-server";
import type { MigrationItem } from "@/api/config-migration";
import type { OrgParentCandidate } from "./ManualUnitDialog";
import type { ReactNode } from "react";
import type { RunForestNode } from "@/api/config-migration";
import type { OrgScriptMode } from "@/api/org-structure-conversation";
import { openOrgUnitConversation } from "@/api/stage-conversations";
import { deleteManualUnit, setScopeVerdict } from "@/api/configuration-server";
import { CbcConnectionDialog } from "@/components/agents/configuration-server/stages/ConnectionDialogs";
import { ManualUnitDialog } from "@/components/agents/configuration-server/ManualUnitDialog";
import { ORG_UNIT_TYPES } from "@/lib/org-unit-forms";
import { OrgNodeDetailDrawer } from "@/components/agents/configuration-server/OrgNodeDetailDrawer";
import { OrgOriginLegend } from "@/components/agents/configuration-server/OrgOriginLegend";
import {
  FILTER_ROW_CLASS,
  FilterSearch,
  SourceSegment,
} from "@/components/agents/configuration-server/stages/DecisionRows";
import {
  ExecutionListCard,
  RunAllButton,
  type ExecutionListRow,
  type ExecutionRowStatus,
} from "@/components/agents/configuration-server/stages/ExecutionListCard";
import { OrgDecisionMap } from "@/components/agents/configuration-server/stages/OrgDecisionMap";
import { useDraftVerdicts } from "@/components/agents/configuration-server/stages/managementDraftContext";
import { orgIdentity, overlayOrgForest } from "@/lib/management-draft";
import { OrgStructureCardsView } from "@/components/agents/configuration-server/OrgStructureCardsView";
import {
  OrgStructureListView,
  type OrgSelection,
} from "@/components/agents/configuration-server/OrgStructureListView";
import {
  ORG_PLAY_TEST_ID,
  ORG_RETRY_STATUSES,
} from "@/components/agents/configuration-server/orgUnitState";
import { ScopeVerdictDialog } from "@/components/agents/configuration-server/ScopeVerdictDialog";
import { Card } from "@/components/brand/Card";
import {
  Toolbar,
  ToolbarSearch,
  ToolbarSpacer,
} from "@/components/brand/Toolbar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  useCbcConnection,
  useMigrationItems,
} from "@/hooks/useConfigMigration";
import { useDebouncedSearch } from "@/hooks/useDebouncedSearch";
import { useOrgStructure } from "@/hooks/useConfigurationServer";
import { errorDetailOr } from "@/lib/api-error";
import {
  otherConversationOpenText,
  toastOpenError,
} from "@/lib/conversation-refusal";
import { previewCascade } from "@/lib/org-structure-cascade";
import { cn } from "@/lib/utils";
import {
  ancestorsInRunForest,
  buildOrgStructureForest,
  buildRunForest,
  computeOrgRunClosureForForest,
  effectiveOrgId,
  extractedValueOf,
  listOrgUnitTypeOptions,
  listOrgCountryOptions,
  filterOrgForest,
  hasOrgNodeWithMigrationStatus,
  executionMigrationStatus,
  isOrgParentNotRegistered,
  orgUnitTypeLabelKey,
  projectOrgRootsForDisplay,
  pruneEmptyOrgNodes,
  resumedConversationOwner,
  runForestEntryOf,
  selectOrgRunQueue,
  uniqueOrgUnits,
  type MigrationStatus,
  type OrgCountryOption,
  type OrgUnitTypeOption,
  type OrgTreeNode,
  type ScopeVerdict,
  type UnitOrigin,
} from "@/lib/org-structure-tree";

export type OrgStructureViewMode = "list" | "cards" | "map";

export type RunQueueState =
  | { kind: "running"; current: number; total: number }
  | { kind: "stopped"; code: string };

const MANAGEMENT_SELECT_CLASS =
  "h-9 min-w-[180px] px-2.5 text-[13px] text-[var(--exed-ink-900)]";

/** Mesmo mapa de `OrgNodeDetailDrawer` — não duplica chave de tradução nova
 *  para o filtro de veredito da toolbar. */
const SCOPE_VERDICT_LABEL: Record<ScopeVerdict, string> = {
  pending: "Pending",
  approved: "Approved",
  out_of_scope: "Out of scope",
};

const SCOPE_VERDICT_FILTER_OPTIONS: ScopeVerdict[] = [
  "pending",
  "approved",
  "out_of_scope",
];

/** Filtro de procedência — as mesmas strings de "Origin" do painel de detalhes. */
const ORIGIN_FILTER_OPTIONS: Array<{ value: UnitOrigin; label: string }> = [
  { value: "sap", label: "Extracted" },
  { value: "manual", label: "Manual" },
];

/** "7003 (company_code)" — a unidade citada no aviso de execução em andamento. */
function runUnitLabel(unit: { item_type: string; item_key: string }): string {
  return `${unit.item_key.split("|").pop()} (${unit.item_type})`;
}

/** Status do cadastro no CBC nos status do cartão da Execução. */
const EXECUTION_ROW_STATUS: Record<MigrationStatus, ExecutionRowStatus> = {
  pending: "idle",
  running: "running",
  completed: "done",
  failed: "failed",
  interrupted: "interrupted",
  awaiting_human_decision: "waiting",
};

/** A floresta em lista plana, na ordem canônica: cada pai antes dos filhos. */
function flattenForest(nodes: OrgTreeNode[]): OrgTreeNode[] {
  return nodes.flatMap((node) => [node, ...flattenForest(node.children)]);
}

/** O nível de cada nó na floresta (0 é a raiz), pela chave. */
function forestDepths(
  nodes: OrgTreeNode[],
  depth = 0,
  into = new Map<string, number>(),
): Map<string, number> {
  for (const node of nodes) {
    into.set(node.key, depth);
    forestDepths(node.children, depth + 1, into);
  }
  return into;
}

function findNodeByKey(roots: OrgTreeNode[], key: string): OrgTreeNode | null {
  for (const node of roots) {
    if (node.key === key) return node;
    const child = findNodeByKey(node.children, key);
    if (child) return child;
  }
  return null;
}

function pruneVerdictBranch(
  nodes: OrgTreeNode[],
  verdict: ScopeVerdict,
): OrgTreeNode[] {
  return nodes.flatMap((node) => {
    const children = pruneVerdictBranch(node.children, verdict);
    if (node.scopeVerdict !== verdict) {
      // A cópia de uma unidade muitos-para-muitos só fica sob um pai que também tem o
      // veredito; senão ela subiria uma vez por pai fora do recorte.
      return children.filter((child) => !child.linkedCopy);
    }
    return [{ ...node, children }];
  });
}

function copiesOf(nodes: OrgTreeNode[]): OrgTreeNode[] {
  return nodes.flatMap((node) => [
    ...(node.linkedCopy ? [node] : []),
    ...copiesOf(node.children),
  ]);
}

/**
 * Só as unidades com o veredito, sem os pais de outro veredito. A unidade muitos-para-muitos
 * (Distribution Channel, Division) fica sob os pais que têm o mesmo veredito e, se nenhum tem,
 * aparece uma vez na raiz, para não sumir da lista.
 */
function pruneVerdictForest(
  nodes: OrgTreeNode[],
  verdict: ScopeVerdict,
): OrgTreeNode[] {
  const kept = pruneVerdictBranch(nodes, verdict);
  const present = new Set(
    flattenForest(kept).map((node) => `${node.levelCode}:${node.itemKey}`),
  );
  const alone = new Map<string, OrgTreeNode>();
  for (const copy of copiesOf(nodes)) {
    const identity = `${copy.levelCode}:${copy.itemKey}`;
    if (
      copy.scopeVerdict === verdict &&
      !present.has(identity) &&
      !alone.has(identity)
    ) {
      alone.set(identity, { ...copy, children: [] });
    }
  }
  return [...kept, ...alone.values()];
}

function MultiSelectFilter({
  options,
  selectedKeys,
  onChange,
  allLabel,
  searchPlaceholder,
  emptyMessage,
  translateLabels = false,
  triggerClassName,
}: {
  triggerClassName?: string;
  options: Array<OrgUnitTypeOption | OrgCountryOption>;
  selectedKeys: string[];
  onChange: (next: string[]) => void;
  allLabel: string;
  searchPlaceholder: string;
  emptyMessage: string;
  translateLabels?: boolean;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const selectedSet = useMemo(() => new Set(selectedKeys), [selectedKeys]);

  useEffect(() => {
    if (!open) {
      setQuery("");
      return;
    }
    requestAnimationFrame(() => inputRef.current?.focus());
  }, [open]);

  const translatedLabelByValue = useMemo(
    () =>
      new Map(
        options.map((option) => [
          option.value,
          translateLabels
            ? t(option.label, { defaultValue: option.label })
            : option.label,
        ]),
      ),
    [options, t, translateLabels],
  );

  const filteredOptions = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) {
      return options.map((option) => ({
        ...option,
        renderedLabel: translatedLabelByValue.get(option.value) ?? option.label,
      }));
    }

    return options
      .map((option) => ({
        ...option,
        renderedLabel: translatedLabelByValue.get(option.value) ?? option.label,
      }))
      .filter((option) =>
        `${option.renderedLabel} ${option.label} ${option.value}`
          .toLowerCase()
          .includes(needle),
      );
  }, [options, query, translatedLabelByValue]);

  const selectedOptions = options.filter((option) =>
    selectedSet.has(option.value),
  );
  const triggerLabel =
    selectedOptions.length === 0
      ? t(allLabel)
      : selectedOptions
          .map(
            (option) =>
              `${translatedLabelByValue.get(option.value) ?? option.label} (${option.count})`,
          )
          .join(", ");

  function toggle(value: string) {
    const next = new Set(selectedSet);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    onChange(Array.from(next));
  }

  function clearSelection() {
    onChange([]);
  }

  function openFromKeyboard(event: React.KeyboardEvent<HTMLButtonElement>) {
    if (
      event.key === "Enter" ||
      event.key === " " ||
      event.key === "ArrowDown"
    ) {
      event.preventDefault();
      setOpen(true);
    }
  }

  function toggleFromKeyboard(
    event: React.KeyboardEvent<HTMLButtonElement>,
    value: string,
  ) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      toggle(value);
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      setOpen(false);
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-label={selectedOptions.length === 0 ? t(allLabel) : undefined}
          className={cn(
            "flex h-8 min-w-[220px] max-w-[420px] items-center justify-between gap-2 rounded-md border border-[#e8ebf0] bg-white px-2 text-left text-xs text-[#596b85]",
            triggerClassName,
          )}
          onKeyDown={openFromKeyboard}
        >
          <span className="truncate">{triggerLabel}</span>
          <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-80 p-0"
        onEscapeKeyDown={() => setOpen(false)}
      >
        <div className="border-b border-[#f2f4f7] px-3 py-2">
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                event.preventDefault();
                setOpen(false);
              }
            }}
            placeholder={t(searchPlaceholder)}
            className="w-full border-0 bg-transparent text-xs text-[#08254c] outline-none placeholder:text-[#7b8799]"
          />
        </div>
        {selectedOptions.length > 0 && (
          <div className="border-b border-[#f2f4f7] p-2">
            <button
              type="button"
              className="text-xs text-[#596b85] underline-offset-2 hover:text-[#08254c] hover:underline"
              onClick={clearSelection}
              onKeyDown={(event) => {
                if (event.key === "Escape") {
                  event.preventDefault();
                  setOpen(false);
                }
              }}
            >
              {t("Clear selection")}
            </button>
          </div>
        )}
        <div className="max-h-[280px] overflow-y-auto p-1">
          {filteredOptions.length === 0 ? (
            <div className="px-3 py-4 text-xs text-[#7b8799]">
              {t(emptyMessage)}
            </div>
          ) : (
            filteredOptions.map((option) => {
              const checked = selectedSet.has(option.value);
              return (
                <button
                  key={option.value}
                  type="button"
                  role="checkbox"
                  aria-checked={checked}
                  className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs text-[#08254c] hover:bg-[#f7f9fc]"
                  onClick={() => toggle(option.value)}
                  onKeyDown={(event) => toggleFromKeyboard(event, option.value)}
                >
                  <span
                    className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border ${
                      checked
                        ? "border-[#08254c] bg-[#08254c] text-white"
                        : "border-[#cfd6e2] bg-white text-transparent"
                    }`}
                  >
                    <Check className="h-3 w-3" />
                  </span>
                  <span className="truncate">{option.renderedLabel}</span>
                  <span className="ml-auto shrink-0 text-[11px] text-[#7b8799]">
                    ({option.count})
                  </span>
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** O que a tela precisa para decidir o roteiro de uma unidade antes de abrir a conversa. */
export interface ScriptModeRequest {
  node: OrgTreeNode;
  unit: RunForestNode;
  ancestors: RunForestNode[];
  /** Cancelada quando quem pediu sai da tela: a decisão pendente deve ser descartada. */
  signal: AbortSignal;
}

/** Devolve o `script_mode` da abertura, ou `null` se a decisão foi cancelada. */
export type ScriptModeResolver = (
  request: ScriptModeRequest,
) => Promise<OrgScriptMode | null>;

/**
 * Hierarquia da estrutura organizacional do cliente, como está configurada no
 * ERP de origem. Somente leitura: o agente não configura o destino.
 */
export function OrgStructureTree({
  projectId,
  artifactId,
  view,
  scopeVerdictLock: scopeVerdictLockProp,
  hideExecution = false,
  hideVerdictActions: hideVerdictActionsProp = false,
  executionOnly = false,
  live = false,
  waitingRunIds,
  onSelectNodeOverride,
  activeNodeKey = null,
  onRunStart,
  onConversationOpened,
  onRunAll,
  runQueue,
  onStopQueue,
  onRunStateChange,
  resolveScriptMode,
  runBlockedReason,
  onManagementChanged,
  onCanonicalNodesChange,
  onRequestManagementVerdict,
  onRequestManagementReturn,
  selection,
  detailSelection,
  tool,
  boundedData,
  boundedMigrationItems,
  externalParentCandidates,
  parentControls,
  onParentLevelChange,
  knownChildCounts,
}: {
  /**
   * Gerenciamento paginado (15/10): a lista vem do servidor em páginas, mas
   * detalhes, edição e criação precisam da árvore inteira (conformidade, pais
   * candidatos). Montado só com `tool`, o componente carrega a árvore e abre a
   * ferramenta pedida, sem desenhar a lista.
   */
  knownChildCounts?: Record<string, number>;
  boundedData?: OrgStructureResponse | null;
  boundedMigrationItems?: MigrationItem[];
  externalParentCandidates?: Record<string, OrgParentCandidate[]>;
  parentControls?: ReactNode;
  onParentLevelChange?: (level: string | null) => void;
  tool?: {
    request: { kind: "details" | "edit"; key: string } | { kind: "create" };
    onClose: () => void;
  };
  projectId?: string;
  artifactId?: string;
  /** Controlado pelo pai: a alternância Cards/List agora vive na barra de
   *  abas do pipeline, não mais como um toggle dentro deste toolbar. */
  view: OrgStructureViewMode;
  scopeVerdictLock?: ScopeVerdict;
  hideExecution?: boolean;
  /** Etapa 3: esconde ✓ aprovar e ⊘ tirar do escopo de cada linha/cartão. */
  hideVerdictActions?: boolean;
  /**
   * Etapa 3 (Execução): só as aprovadas, sem CRUD (criar, editar, excluir) e sem
   * veredito; dos filtros fica só a busca. Mantém o selo do CBC e o "Run all".
   */
  executionOnly?: boolean;
  /** Conversa aberta: consulta de reserva da lista a cada 30 segundos. */
  live?: boolean;
  /** Etapa 3: runs cuja conversa espera a resposta do consultor. A linha da unidade
   *  diz "Waiting for you" em vez de "Running", como o console. */
  waitingRunIds?: ReadonlySet<string>;
  /** Etapa 3: a linha chama isto em vez de abrir o detalhe da unidade. */
  onSelectNodeOverride?: (node: OrgTreeNode) => void;
  /** Etapa 3: a unidade aberta no console; a linha dela fica destacada. */
  activeNodeKey?: string | null;
  /** Etapa 3: a execução de uma unidade começou (play, plano ou "Run all"); a tela
   *  seleciona a unidade e abre o terminal na hora. */
  onRunStart?: (node: OrgTreeNode) => void;
  /** A conversa aberta já pode aparecer no console antes da próxima leitura da visão geral.
   *  `node` é a unidade dona dela: a religada pode ser de outra unidade, não da clicada. */
  onConversationOpened?: (runId: string, node: OrgTreeNode) => void;
  /** A tela executa o conjunto aprovado completo, em ordem pai→filho. */
  onRunAll?: (nodes: OrgTreeNode[]) => void;
  /** A fila do "Run all" andando (posição, total) ou parada numa unidade: o cabeçalho
   *  mostra o estado dela e troca o "Run all" pelo "Stop queue". */
  runQueue?: RunQueueState | null;
  onStopQueue?: () => void;
  /** Etapa 3: um run desta árvore começou ou terminou (qualquer disparo, inclusive
   *  "Run all"); a tela relê a visão geral para liberar as abas sem recarregar. */
  onRunStateChange?: () => void;
  /**
   * Etapa 3: antes de abrir a conversa de uma unidade, a tela consulta se há roteiro
   * pronto e, havendo, mostra a oferta no console. Devolve o `script_mode` da abertura,
   * ou `null` quando a decisão foi cancelada (nada abre). Sem isto o play abre direto
   * em `auto`, como antes.
   */
  resolveScriptMode?: ScriptModeResolver;
  /** Etapa 3: outra execução no projeto trava todo play e o "Run all" com este motivo. */
  runBlockedReason?: string | null;
  /** A central atualiza seus contadores após decisões ou edições de unidades. */
  onManagementChanged?: () => void;
  /** Etapa 3: todas as unidades da árvore canônica, mesmo as que a busca esconde. */
  onCanonicalNodesChange?: (nodes: OrgTreeNode[]) => void;
  onRequestManagementVerdict?: (
    node: OrgTreeNode,
    verdict: Exclude<ScopeVerdict, "pending">,
  ) => void;
  /** Mapa do Gerenciamento: "Undo" de uma unidade decidida. */
  onRequestManagementReturn?: (node: OrgTreeNode) => void;
  selection?: OrgSelection;
  /** Permite abrir o mesmo drawer a partir da lista de decididos. */
  detailSelection?: {
    key: string | null;
    onChange: (key: string | null) => void;
  };
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const structureQuery = useOrgStructure(
    projectId,
    artifactId,
    boundedData === undefined,
  );
  const data =
    boundedData !== undefined
      ? (boundedData ?? undefined)
      : structureQuery.data;
  const isLoading =
    boundedData !== undefined ? boundedData === null : structureQuery.isLoading;
  const isError = boundedData !== undefined ? false : structureQuery.isError;
  const scopeVerdictLock: ScopeVerdict | undefined = executionOnly
    ? "approved"
    : scopeVerdictLockProp;
  const hideVerdictActions = executionOnly || hideVerdictActionsProp;

  // Fonte única do gate de conexão CBC — vale para o play de cada linha/card,
  // para o "e todos os filhos" e para o "Run all". `useCbcConnection` guarda
  // com `enabled: Boolean(projectId)`, então sem projeto não dispara nada.
  const { data: cbcConnection } = useCbcConnection(projectId);
  // `cbcConnected` (credencial gravada) só libera o botão do play na tela e tira o selo
  // amarelo. Para EXECUTAR não basta: o play (POST /conversations) recusa com 412
  // `cbc_connection_not_tested` se o CBC do usuário nunca passou no teste de conexão.
  // Validada (`last_login_at`, carimbado quando o CBC respondeu autenticado, por teste ou
  // por execução real) deixa a tag verde em vez de neutra.
  const cbcConnected = cbcConnection?.configured === true;
  const cbcValidated = cbcConnected && Boolean(cbcConnection?.last_login_at);
  // O "Configure here" do selo amarelo da Execução abre o modal do CBC. Quem o fecha é o
  // Save do formulário (`onSaved`), nunca a releitura: o Test grava a conexão antes de
  // logar, então `cbcConnected` vira true com o teste ainda falhando, e fechar por ele
  // escondia o resultado do teste.
  const [cbcDialogOpen, setCbcDialogOpen] = useState(false);

  // Status de cadastro por unidade — sem isto todo nó nascia "pending" para
  // sempre, e os quatro estados além de "pending" (running/completed/failed/
  // interrupted) nunca apareciam mesmo com dados reais no banco.
  const { data: migrationItemsData } = useMigrationItems(
    boundedData !== undefined ? undefined : projectId,
    { live },
  );
  const migrationItems =
    boundedData !== undefined
      ? boundedMigrationItems
      : migrationItemsData?.items;

  // A trava de execução no backend é por PROJETO, não por item: uma conversa
  // aberta por vez contra o mesmo tenant CBC (`profile_busy` em
  // services/agents/app/services/config_migration/repository.py). Um item
  // rodando pode estar FORA do filtro de tipo/país visível na tela (ex.: uma
  // Company rodando enquanto a pessoa olha só Company Code) — sem isto, o play
  // de qualquer outro item continuava clicável e a pessoa só descobria o
  // bloqueio depois de clicar, com um 409 sem contexto nenhum.
  const runInProgressItem = useMemo(
    () => migrationItems?.find((item) => item.status === "running"),
    [migrationItems],
  );
  // Unidades cujo play acabou de ser clicado, até a execução terminar: o
  // status "running" leva um instante para chegar (SSE + refetch), e sem isto
  // o play ficava igual nesse intervalo — sem retorno de que o clique pegou.
  const [dispatchingUnitKeys, setDispatchingUnitKeys] = useState<
    ReadonlySet<string>
  >(() => new Set());
  const [dispatchingLabel, setDispatchingLabel] = useState<string | undefined>(
    undefined,
  );
  const runInProgressLabel = runInProgressItem
    ? runUnitLabel(runInProgressItem)
    : dispatchingLabel;

  const [search, setSearch] = useState("");
  // O filtro espera 3 s (Enter aplica na hora); a caixa mostra o que a pessoa digitou.
  const [appliedSearch, commitSearch] = useDebouncedSearch(search);
  const [selectedUnitTypes, setSelectedUnitTypes] = useState<string[]>([]);
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  // Só 3 valores fixos (diferente de tipo/país, que vêm dinâmicos dos dados)
  // — um grupo de toggles simples cobre sem precisar de `MultiSelectFilter`
  // nem de um `<select>`/combobox novo, que ficaria ambíguo com o combobox
  // sem nome que `getByRole("combobox")` já busca no filtro de tipo (linha
  // ~160 abaixo, `role="combobox"` do `MultiSelectFilter`).
  const [selectedScopeVerdict, setSelectedScopeVerdict] = useState<
    ScopeVerdict | undefined
  >(undefined);
  // Mesmo formato do filtro de veredito (grupo de toggles), pela mesma razão:
  // dois valores fixos, e um `<select>` seria um segundo combobox na toolbar.
  const [selectedOrigin, setSelectedOrigin] = useState<UnitOrigin | undefined>(
    undefined,
  );
  // Liga/desliga: só existe um status filtrável ("aguardando decisão"), então
  // basta um booleano — o chip só é renderizado quando há alguma unidade assim.
  const [awaitingDecisionOnly, setAwaitingDecisionOnly] = useState(false);
  const [localSelectedNodeKey, setLocalSelectedNodeKey] = useState<
    string | null
  >(null);
  const selectedNodeKey = detailSelection
    ? detailSelection.key
    : localSelectedNodeKey;
  const setSelectedNodeKey =
    detailSelection?.onChange ?? setLocalSelectedNodeKey;

  // Sem este memo, `prunedRoots` ganhava identidade nova a CADA render, o que
  // invalidava displayRoots → unitTypeOptions → availableUnitTypeKeys e fazia
  // os dois efeitos de limpeza reagirem toda vez: os memos abaixo nunca
  // acertavam o cache. (Os efeitos devolvem `current` quando nada muda, então
  // não havia loop — mas também não havia memoização nenhuma.)
  const levels = data?.levels;
  // No Gerenciamento a árvore mostra o rascunho por cima do servidor.
  const draftVerdicts = useDraftVerdicts("org_structure");
  const canonicalRoots = useMemo(
    () =>
      overlayOrgForest(
        buildOrgStructureForest(levels ?? [], migrationItems ?? []).roots,
        draftVerdicts,
      ),
    [levels, migrationItems, draftVerdicts],
  );
  const prunedRoots = useMemo(
    () => pruneEmptyOrgNodes(canonicalRoots),
    [canonicalRoots],
  );
  // Cada linha da Execução pergunta pelo pai: um mapa evita uma busca na árvore por linha.
  const prunedByKey = useMemo(() => {
    const byKey = new Map<string, OrgTreeNode>();
    for (const node of flattenForest(prunedRoots)) byKey.set(node.key, node);
    return byKey;
  }, [prunedRoots]);
  const findPrunedNode = (key: string) => prunedByKey.get(key);
  const displayRoots = useMemo(
    () => projectOrgRootsForDisplay(prunedRoots),
    [prunedRoots],
  );
  // Olha a árvore inteira (não a filtrada): com o chip ativo a lista encolhe,
  // e a condição não pode depender do que o próprio chip esconde.
  const hasAwaitingDecision = useMemo(
    () =>
      hasOrgNodeWithMigrationStatus(displayRoots, "awaiting_human_decision"),
    [displayRoots],
  );
  const unitTypeOptions = useMemo(
    () => listOrgUnitTypeOptions(displayRoots),
    [displayRoots],
  );
  const countryOptions = useMemo(
    () => listOrgCountryOptions(displayRoots),
    [displayRoots],
  );
  // Opções de nível para o seletor de "Create unit" (#845): sai de
  // `data.levels` (o cookbook inteiro, inclusive nível ainda sem nenhum nó
  // extraído), não de `unitTypeOptions` — que só lista nível com dado, e um
  // nível vazio é exatamente onde criar a PRIMEIRA unidade manual faz
  // sentido. Restrito aos 9 tipos do catálogo do CBC que aceitam unidade
  // manual (#899) — os demais (sales_area, division, operating_concern, …)
  // nem aparecem no seletor: criar unidade manual neles seria 422 na hora.
  const createUnitLevelOptions = useMemo(
    () =>
      (levels ?? [])
        .filter((level) => ORG_UNIT_TYPES.has(level.level_code))
        .slice()
        .sort((a, b) => a.sequence - b.sequence)
        .map((level) => ({
          levelCode: level.level_code,
          levelName: level.level_name,
        })),
    [levels],
  );
  // Para o select de pai do diálogo (#899): o `parent_level_code` de CADA
  // nível do cookbook (não só os 9 criáveis — um tipo criável pode ter pai
  // fora dos 9, ex.: sales_office → sales_area) e as unidades candidatas a
  // pai em cada nível, extraídas + manuais (a mesma `levels`, que já traz as
  // duas procedências).
  const parentLevelCodeByType = useMemo(
    () =>
      Object.fromEntries(
        (levels ?? []).map((level) => [
          level.level_code,
          level.parent_level_code,
        ]),
      ),
    [levels],
  );
  const parentCandidatesByLevel = useMemo(
    () =>
      Object.fromEntries(
        (levels ?? []).map((level) => [
          level.level_code,
          level.nodes.map((node) => ({
            id: node.id,
            name: node.name,
            targetId: node.target_id,
          })),
        ]),
      ),
    [levels],
  );
  const availableUnitTypeKeys = useMemo(
    () => new Set(unitTypeOptions.map((option) => option.value)),
    [unitTypeOptions],
  );
  const availableCountryKeys = useMemo(
    () => new Set(countryOptions.map((option) => option.value)),
    [countryOptions],
  );

  // O polling pode tirar a última unidade de "aguardando decisão" com o chip
  // ativo: o chip some e a lista ficaria vazia sem caminho de volta.
  useEffect(() => {
    if (!hasAwaitingDecision) setAwaitingDecisionOnly(false);
  }, [hasAwaitingDecision]);

  useEffect(() => {
    setSelectedUnitTypes((current) => {
      const next = current.filter((key) => availableUnitTypeKeys.has(key));
      return next.length === current.length ? current : next;
    });
  }, [availableUnitTypeKeys]);

  useEffect(() => {
    setSelectedCountries((current) => {
      const next = current.filter((key) => availableCountryKeys.has(key));
      return next.length === current.length ? current : next;
    });
  }, [availableCountryKeys]);

  // Memorizados: sem isso cada render (marcar uma caixa, inclusive) refazia o
  // filtro de milhares de unidades e disparava de novo o aviso de visíveis.
  const filteredRoots = useMemo(
    () =>
      filterOrgForest(scopeVerdictLock ? prunedRoots : displayRoots, {
        search: appliedSearch,
        unitTypeKeys: selectedUnitTypes,
        countryCodes: selectedCountries,
        scopeVerdict: scopeVerdictLock ?? selectedScopeVerdict,
        origin: selectedOrigin,
        migrationStatus: awaitingDecisionOnly
          ? "awaiting_human_decision"
          : undefined,
      }),
    [
      scopeVerdictLock,
      prunedRoots,
      displayRoots,
      appliedSearch,
      selectedUnitTypes,
      selectedCountries,
      selectedScopeVerdict,
      selectedOrigin,
      awaitingDecisionOnly,
    ],
  );
  const lockedRoots = useMemo(
    () =>
      scopeVerdictLock
        ? pruneEmptyOrgNodes(
            pruneVerdictForest(filteredRoots, scopeVerdictLock),
          )
        : filteredRoots,
    [filteredRoots, scopeVerdictLock],
  );
  // Gerenciamento (etapa 2): filtros, linhas e cards do Claude Design.
  const management = hideExecution && !!selection;
  const filtering =
    Boolean(appliedSearch.trim()) ||
    selectedUnitTypes.length > 0 ||
    selectedCountries.length > 0 ||
    !!selectedOrigin;
  // Mapa: a árvore inteira (todas as decisões); os filtros só apagam o que não bate.
  const mapMatchKeys = useMemo(() => {
    if (!management || view !== "map" || !filtering) return null;
    const keys = new Set<string>();
    const collect = (nodes: OrgTreeNode[]) => {
      for (const node of nodes) {
        keys.add(node.key);
        collect(node.children);
      }
    };
    collect(
      filterOrgForest(canonicalRoots, {
        search: appliedSearch,
        unitTypeKeys: selectedUnitTypes,
        countryCodes: selectedCountries,
        origin: selectedOrigin,
      }),
    );
    return keys;
  }, [
    management,
    view,
    filtering,
    canonicalRoots,
    appliedSearch,
    selectedUnitTypes,
    selectedCountries,
    selectedOrigin,
  ]);
  useEffect(() => {
    if (!onCanonicalNodesChange) return;
    const flatten = (nodes: OrgTreeNode[]): OrgTreeNode[] =>
      nodes.flatMap((node) => [node, ...flatten(node.children)]);
    onCanonicalNodesChange(flatten(prunedRoots));
  }, [prunedRoots, onCanonicalNodesChange]);
  // Decididos podem incluir unidades sem nome, omitidas da projeção visual.
  const detailRoots = detailSelection || tool ? canonicalRoots : prunedRoots;
  const selectedNode = useMemo(
    () =>
      selectedNodeKey ? findNodeByKey(detailRoots, selectedNodeKey) : null,
    [detailRoots, selectedNodeKey],
  );
  // Execução: o botão de detalhes da linha abre o drawer só de leitura, sem selecionar a linha
  // (a seleção é o que abre o console do agente).
  const [executionDetailKey, setExecutionDetailKey] = useState<string | null>(
    null,
  );
  const executionDetailNode = useMemo(
    () =>
      executionOnly && executionDetailKey
        ? findNodeByKey(detailRoots, executionDetailKey)
        : null,
    [executionOnly, executionDetailKey, detailRoots],
  );

  // O que o "Run all" dispara: as unidades APROVADAS do recorte visível.
  //
  // O recorte de escopo é a floresta FILTRADA da tela (se os filtros
  // restringem a Company Code + US, o "Run all" é sobre isso), mas o fecho
  // sai da árvore CANÔNICA — a projeção de exibição consolida uma Company no
  // cartão do Company Code homônimo, e percorrer só o que está na tela
  // deixaria essa Company de fora da contagem E do disparo.
  //
  // O filtro por veredito vem depois do fecho, e não por `filterOrgForest`:
  // aquele é filtro de exibição e mantém o pai de um nó que casa para não
  // esconder o filho, o que arrastaria unidade pendente para o disparo.
  // Aprovar cascateia para cima, então todo aprovado já tem os ancestrais
  // aprovados — ficar só com os aprovados não deixa pai para trás.
  const approvedNodes = useMemo(() => {
    const selected = new Set(
      computeOrgRunClosureForForest(filteredRoots, prunedRoots).map(
        (node) => node.key,
      ),
    );
    const flat = flattenForest(prunedRoots);
    return selectOrgRunQueue(
      flat.filter((node) => selected.has(node.key)),
      new Map(flat.map((node) => [node.key, node])),
    );
  }, [filteredRoots, prunedRoots]);
  const approvedUnits = useMemo(
    () =>
      approvedNodes.map((node) => ({
        item_type: node.levelCode,
        item_key: node.itemKey,
      })),
    [approvedNodes],
  );

  // A contagem do diálogo é o que será disparado — anunciar um número e
  // disparar outro é pior do que não anunciar nada.
  const runAllUnitCount = approvedUnits.length;

  // Veredito de escopo (#845) pendente de confirmação em `ScopeVerdictDialog`
  // — espera a confirmação de aprovar/tirar do escopo.
  const [scopeTarget, setScopeTarget] = useState<{
    node: OrgTreeNode;
    verdict: Exclude<ScopeVerdict, "pending">;
    affected: OrgTreeNode[];
  } | null>(null);
  const [unitDialog, setUnitDialog] = useState<
    | { mode: "create"; levelCode: string }
    | { mode: "edit"; node: OrgTreeNode }
    | null
  >(null);

  // Ferramenta pedida pelo Gerenciamento paginado: abre quando a árvore chega.
  const toolRequest = tool?.request;
  const openedTool = useRef<typeof toolRequest>(undefined);
  useEffect(() => {
    if (!toolRequest || openedTool.current === toolRequest) return;
    if (toolRequest.kind === "create") {
      if (!createUnitLevelOptions.length) return;
      openedTool.current = toolRequest;
      if (createUnitLevelOptions.length)
        setUnitDialog({
          mode: "create",
          levelCode: createUnitLevelOptions[0].levelCode,
        });
      return;
    }
    if (!canonicalRoots.length) return;
    openedTool.current = toolRequest;
    const all = (nodes: OrgTreeNode[]): OrgTreeNode[] =>
      nodes.flatMap((node) => [node, ...all(node.children)]);
    const target = all(canonicalRoots).find(
      (node) => orgIdentity(node) === toolRequest.key,
    );
    if (!target) return;
    if (toolRequest.kind === "details") setSelectedNodeKey(target.key);
    else setUnitDialog({ mode: "edit", node: target });
  }, [toolRequest, canonicalRoots, createUnitLevelOptions, setSelectedNodeKey]);

  async function applyVerdict(
    target: OrgTreeNode,
    verdict: Exclude<ScopeVerdict, "pending">,
  ) {
    if (!projectId) return;
    // `ScopeVerdictDialog.onConfirm` fecha o modal LOGO depois de chamar esta
    // função (`void applyVerdict(...)`, sem esperar) — sem o catch, uma falha
    // de rede/403/404 fechava a confirmação como se tivesse dado certo, sem
    // aviso nenhum: a pessoa só notava depois, vendo a unidade no estado
    // antigo. `errorDetailOr` (não `describeOpenError`, que é do formato de
    // erro da abertura da conversa) é o extrator certo aqui — `api`
    // devolve erro REST padrão do FastAPI/axios.
    try {
      await setScopeVerdict(
        projectId,
        target.levelCode,
        target.itemKey,
        verdict,
      );
      await queryClient.invalidateQueries({
        queryKey: ["config-server", "org-structure", projectId],
      });
      onManagementChanged?.();
      // Sem isto a única mudança na tela era o botão trocar de ícone — não
      // dava para saber se a decisão tinha sido gravada.
      toast.success(
        t(
          verdict === "approved"
            ? "{{unit}} approved."
            : "{{unit}} taken out of scope.",
          {
            unit: effectiveOrgId(target),
          },
        ),
      );
    } catch (error) {
      toast.error(
        errorDetailOr(error, t("Could not update the scope verdict.")),
      );
    }
  }

  function requestVerdict(
    target: OrgTreeNode,
    verdict: Exclude<ScopeVerdict, "pending">,
  ) {
    if (onRequestManagementVerdict) {
      onRequestManagementVerdict(target, verdict);
      return;
    }
    // A prévia roda sobre `prunedRoots` (árvore CANÔNICA), nunca
    // `displayRoots`/`filteredRoots`: a mesma razão de `handleRunNode` abaixo
    // — a projeção de exibição consolida a Company no cartão do Company Code
    // homônimo, e calcular a cascata sobre ela mentiria sobre o efeito real.
    // Sem efeito colateral, `ScopeVerdictDialog` não renderiza nada (Task
    // 12) — então é seguro gravar direto sem abrir o modal.
    const affected = previewCascade(target, verdict, prunedRoots);
    if (affected.length === 0) {
      void applyVerdict(target, verdict);
      return;
    }
    // A prévia calculada aqui vai junto para o diálogo: recalcular lá dentro
    // percorria a floresta canônica uma segunda vez a cada clique, e deixava
    // a regra de "quando mostrar o modal" duplicada em dois arquivos.
    setScopeTarget({ node: target, verdict, affected });
  }

  async function handleDeleteUnit(target: OrgTreeNode) {
    if (!projectId) return;
    if (!target.migrationItemId) {
      // Sem a linha de gerenciamento não há o que excluir. Sair calado fazia
      // o diálogo fechar como se tivesse funcionado.
      toast.error(t("Could not delete this unit."));
      return;
    }
    // O `AlertDialogAction` do drawer fecha o diálogo de confirmação no
    // clique, mesmo sem `preventDefault` — mesmo risco de `applyVerdict`
    // acima: sem o catch, uma falha aqui parecia sucesso silencioso.
    try {
      await deleteManualUnit(projectId, target.migrationItemId);
      await queryClient.invalidateQueries({
        queryKey: ["config-server", "org-structure", projectId],
      });
      onManagementChanged?.();
      setSelectedNodeKey(null);
      toast.success(t("{{unit}} deleted.", { unit: effectiveOrgId(target) }));
    } catch (error) {
      toast.error(errorDetailOr(error, t("Could not delete this unit.")));
    }
  }

  // O disparo vivo (a abertura da conversa em andamento): guardar a referência
  // deduplica disparos concorrentes (play segurado, duplo clique) e deixa o unmount
  // descartar o resultado de uma abertura que terminar depois dele.
  const runControllerRef = useRef<AbortController | null>(null);

  useEffect(() => () => runControllerRef.current?.abort(), []);

  /**
   * Abre a conversa da unidade do disparo (o motor de conversas não tem lote). Falha
   * vira toast com a frase da recusa; ao abrir, a lista e a visão geral se relêem.
   * A conversa religada que é de outra unidade vai para a dona dela, com o aviso,
   * nunca para a clicada: o consultor responderia à conversa errada.
   */
  function runOrgUnit(node: OrgTreeNode) {
    if (!projectId || runControllerRef.current) return;
    onRunStart?.(node);
    // A árvore canônica preserva os ancestrais consolidados apenas na exibição.
    const forest = buildRunForest(prunedRoots);
    const invalidateItems = () =>
      queryClient.invalidateQueries({
        queryKey: ["migration-items", projectId],
      });
    const clearDispatching = () => {
      setDispatchingUnitKeys(new Set());
      setDispatchingLabel(undefined);
    };
    setDispatchingUnitKeys(new Set([`${node.levelCode}:${node.itemKey}`]));
    setDispatchingLabel(
      runUnitLabel({ item_type: node.levelCode, item_key: node.itemKey }),
    );

    const unit = runForestEntryOf(forest, node);
    if (!unit) {
      clearDispatching();
      console.error(
        "Unidade fora da floresta canônica ao abrir a conversa",
        node.levelCode,
        node.itemKey,
      );
      toast.error(
        t(
          "Unit {{code}} is no longer in the organizational structure. Reload the page and try again.",
          {
            code: node.itemKey,
          },
        ),
      );
      return;
    }
    // Só guarda o "disparo vivo" (deduplicação e limpeza ao desmontar).
    const controller = new AbortController();
    runControllerRef.current = controller;
    const ancestors = ancestorsInRunForest(forest, unit);
    const deciding: Promise<OrgScriptMode | null> = resolveScriptMode
      ? resolveScriptMode({ node, unit, ancestors, signal: controller.signal })
      : Promise.resolve("auto");
    // A oferta do roteiro vem antes: a conversa só abre com a decisão do consultor.
    void deciding
      .then((mode) =>
        mode === null
          ? null
          : openOrgUnitConversation(projectId, unit, ancestors, mode),
      )
      .then((opened) => {
        if (controller.signal.aborted) return;
        runControllerRef.current = null;
        clearDispatching();
        if (!opened) return;
        invalidateItems();
        onRunStateChange?.();
        const owner = opened.resumed
          ? resumedConversationOwner(prunedByKey.values(), opened)
          : node;
        if (owner?.key === node.key) {
          onConversationOpened?.(opened.runId, node);
          return;
        }
        toast.info(
          otherConversationOpenText(
            t,
            owner ? effectiveOrgId(owner) : opened.activityCode,
          ),
        );
        if (owner) onConversationOpened?.(opened.runId, owner);
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        runControllerRef.current = null;
        clearDispatching();
        console.error(
          "Falha ao abrir a conversa da estrutura organizacional",
          error,
        );
        invalidateItems();
        onRunStateChange?.();
        toastOpenError(t, error, "org_structure", projectId);
      });
  }

  function runAllVisible() {
    onRunAll?.(approvedNodes);
  }

  function handleRunNode(node: OrgTreeNode) {
    if (isOrgParentNotRegistered(node, findPrunedNode)) return;
    runOrgUnit(node);
  }

  // Execução (etapa 3): o cartão do Claude Design lista as aprovadas do recorte em
  // lista plana; o selo conta as aprovadas da árvore inteira, não as que a busca mostra.
  const executionNodes = useMemo(
    () => (executionOnly ? flattenForest(lockedRoots) : []),
    [executionOnly, lockedRoots],
  );
  const executionCounts = useMemo(() => {
    if (!executionOnly) return { done: 0, total: 0 };
    const approved = uniqueOrgUnits(flattenForest(prunedRoots)).filter(
      (node) => node.scopeVerdict === "approved",
    );
    return {
      done: approved.filter(
        (node) => executionMigrationStatus(node) === "completed",
      ).length,
      total: approved.length,
    };
  }, [executionOnly, prunedRoots]);
  const executionDepths = useMemo(
    () => forestDepths(lockedRoots),
    [lockedRoots],
  );

  const closeTool = () => tool?.onClose();
  const overlays = (
    <>
      <OrgNodeDetailDrawer
        hideExecution={hideExecution}
        hideCrud={executionOnly}
        node={executionDetailNode ?? selectedNode}
        onClose={() => {
          setExecutionDetailKey(null);
          setSelectedNodeKey(null);
          closeTool();
        }}
        actions={
          executionDetailNode
            ? undefined
            : {
                cbcConnected,
                runInProgressLabel,
                dispatchingUnitKeys,
                onRunNode: hideExecution ? undefined : handleRunNode,
                onRequestVerdict: requestVerdict,
                hideVerdict: hideVerdictActions,
                onEditUnit: (target) =>
                  setUnitDialog({ mode: "edit", node: target }),
                onDeleteUnit: handleDeleteUnit,
              }
        }
      />
      <ScopeVerdictDialog
        open={scopeTarget !== null}
        node={scopeTarget?.node ?? null}
        affected={scopeTarget?.affected ?? []}
        verdict={scopeTarget?.verdict ?? null}
        onConfirm={() => {
          if (scopeTarget)
            void applyVerdict(scopeTarget.node, scopeTarget.verdict);
          setScopeTarget(null);
        }}
        onCancel={() => setScopeTarget(null)}
      />
      {projectId && !executionOnly && (
        <ManualUnitDialog
          open={unitDialog !== null}
          mode={unitDialog?.mode ?? "create"}
          projectId={projectId}
          levelCode={
            unitDialog?.mode === "create"
              ? unitDialog.levelCode
              : (unitDialog?.node.levelCode ?? "")
          }
          node={unitDialog?.mode === "edit" ? unitDialog.node : undefined}
          levelOptions={createUnitLevelOptions}
          parentLevelCodeByType={parentLevelCodeByType}
          parentCandidatesByLevel={
            externalParentCandidates ?? parentCandidatesByLevel
          }
          hasChildren={
            unitDialog?.mode === "edit" && knownChildCounts
              ? (knownChildCounts[
                  `${unitDialog.node.levelCode}|${unitDialog.node.itemKey}`
                ] ?? 0) > 0
              : undefined
          }
          parentControls={parentControls}
          onParentLevelChange={onParentLevelChange}
          onSaved={() => {
            onManagementChanged?.();
            void queryClient.invalidateQueries({
              queryKey: ["config-server", "org-structure", projectId],
            });
          }}
          onOpenChange={(open) => {
            if (!open) {
              setUnitDialog(null);
              if (!selectedNodeKey) closeTool();
            }
          }}
        />
      )}
    </>
  );
  if (tool) {
    return (
      <>
        {!canonicalRoots.length && isLoading && (
          <p role="status" className="sr-only">
            {t("Loading the hierarchy…")}
          </p>
        )}
        {overlays}
      </>
    );
  }

  if (executionOnly) {
    const selectNode = (node: OrgTreeNode) =>
      onSelectNodeOverride
        ? onSelectNodeOverride(node)
        : setSelectedNodeKey(node.key);
    // As mesmas regras do play de `OrgUnitRowActions`: cadastrada não roda de novo,
    // aguardando decisão responde no terminal, rodando gira, e o motivo vem na ordem
    // em que a pessoa precisa resolver.
    const orgRow = (node: OrgTreeNode): ExecutionListRow => {
      const code = effectiveOrgId(node);
      // O ID editado leva ao lado o da origem; sem edição, uma tag só.
      const original = extractedValueOf(node, "id")?.value ?? null;
      const originalCode = original && original !== code ? original : undefined;
      // A Company não tem ▶: nasce no formulário do Company Code, que a cadastra junto.
      const createdWithCode = node.levelCode === "company";
      const rowStatus = executionMigrationStatus(node);
      const busy =
        rowStatus !== "completed" &&
        (rowStatus === "running" ||
          dispatchingUnitKeys.has(`${node.levelCode}:${node.itemKey}`));
      const playable =
        !createdWithCode &&
        (busy ||
          (node.scopeVerdict === "approved" &&
            node.migrationStatus !== "completed" &&
            node.migrationStatus !== "awaiting_human_decision"));
      const label = t(
        ORG_RETRY_STATUSES.has(node.migrationStatus) && !busy ? "Retry" : "Run",
      );
      const blockedTitle = busy
        ? undefined
        : !cbcConnected
          ? t("CBC connection required to run the agent")
          : runInProgressLabel
            ? t(
                "A registration is already running for this project ({{label}}) — wait for it to finish.",
                {
                  label: runInProgressLabel,
                },
              )
            : (runBlockedReason ??
              (isOrgParentNotRegistered(node, findPrunedNode)
                ? t("Register the parent first, or use Run all.")
                : undefined));
      return {
        key: node.key,
        code,
        originalCode,
        name: node.name ?? (
          <span className="text-[#7b8799]" aria-label={t("No name")}>
            —
          </span>
        ),
        typeLabel: t(orgUnitTypeLabelKey(node.unitTypeLabel, node.levelName), {
          defaultValue: node.levelName,
        }),
        status: busy
          ? node.lastRunId && waitingRunIds?.has(node.lastRunId)
            ? "waiting"
            : "running"
          : EXECUTION_ROW_STATUS[rowStatus],
        statusLabel:
          createdWithCode && !busy && rowStatus === "pending"
            ? t("Created with its Company Code")
            : undefined,
        depth: executionDepths.get(node.key) ?? 0,
        testId: `org-node-${node.key}`,
        details: {
          label: t("View details for {{id}}", { id: code }),
          onDetails: () => setExecutionDetailKey(node.key),
        },
        run: playable
          ? {
              label,
              title: blockedTitle ?? label,
              disabled: blockedTitle !== undefined,
              busy,
              testId: ORG_PLAY_TEST_ID[busy ? "running" : node.migrationStatus],
              onRun: () => handleRunNode(node),
            }
          : null,
      };
    };
    const body = isLoading ? (
      <div className="flex items-center justify-center gap-2 px-5 py-8 text-[12px] text-[#596b85]">
        <Loader2
          aria-hidden="true"
          className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none"
        />
        {t("Loading the hierarchy…")}
      </div>
    ) : isError ? (
      <div
        role="alert"
        className="flex items-center justify-center gap-2 px-5 py-8 text-[12px] text-red-700"
      >
        <AlertTriangle aria-hidden="true" className="h-3.5 w-3.5" />
        {t("Could not load the hierarchy")}
      </div>
    ) : displayRoots.length === 0 ? (
      <p className="m-0 px-5 py-8 text-center text-sm text-[#596b85]">
        {t("No data extracted")}
      </p>
    ) : undefined;
    return (
      <>
        <ExecutionListCard
          overline={t("READ FROM YOUR ERP")}
          title={t("Hierarchy as configured today")}
          done={executionCounts.done}
          total={executionCounts.total}
          connection={
            cbcValidated
              ? {
                  tone: "ok",
                  label: t("Connected to CBC"),
                  url: cbcConnection?.base_url,
                  openLabel: t("Open CBC in a new tab"),
                }
              : cbcConnected
                ? {
                    tone: "neutral",
                    label: t("CBC configured · connection not tested"),
                    url: cbcConnection?.base_url,
                    openLabel: t("Open CBC in a new tab"),
                  }
                : {
                    tone: "warn",
                    label: t("CBC not configured"),
                    configureLabel: t("Configure the CBC connection"),
                    onConfigure: projectId
                      ? () => setCbcDialogOpen(true)
                      : undefined,
                  }
          }
          search={search}
          onSearchChange={setSearch}
          onSearchEnter={commitSearch}
          toolbarActions={
            projectId && (
              <RunAllControls
                runAllUnitCount={runAllUnitCount}
                cbcConnected={cbcConnected}
                runInProgressLabel={runInProgressLabel}
                runBlockedReason={runBlockedReason}
                onConfirmRunAll={runAllVisible}
                runQueue={runQueue}
                onStopQueue={onStopQueue}
              />
            )
          }
          rows={executionNodes.map(orgRow)}
          selectedKey={activeNodeKey}
          onSelect={(key) => {
            const node = executionNodes.find(
              (candidate) => candidate.key === key,
            );
            if (node) selectNode(node);
          }}
          body={body}
        />
        {overlays}
        {projectId && (
          <CbcConnectionDialog
            projectId={projectId}
            open={cbcDialogOpen}
            onOpenChange={setCbcDialogOpen}
          />
        )}
      </>
    );
  }

  return (
    // Sempre montado imediatamente sob a barra de abas (import do MCP), que
    // já fecha o topo — sem isso o canto arredondado duplica a borda.
    <Card
      className={
        hideExecution
          ? "rounded-none border-0 bg-transparent shadow-none"
          : "rounded-t-none border-t-0"
      }
    >
      {!hideExecution && (
        <div className="flex items-start justify-between gap-3 px-4 pb-2 pt-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.1em] text-[#7b8799]">
              {t("READ FROM YOUR ERP")}
            </div>
            <h3 className="m-0 mt-1 font-brand text-[16px] font-bold text-[#08254c]">
              {t("Hierarchy as configured today")}
            </h3>
          </div>
          {!hideExecution &&
            (cbcValidated ? (
              <Badge className="shrink-0 gap-1 border-green-600/30 bg-green-50 text-green-800 hover:bg-green-50">
                <CheckCircle2 className="h-3 w-3" />
                {t("Connected to CBC")}
              </Badge>
            ) : cbcConnected ? (
              <Badge className="shrink-0 gap-1 border-[#cfd6e2] bg-[#f2f4f7] text-[#596b85] hover:bg-[#f2f4f7]">
                <PlugZap className="h-3 w-3" />
                {t("CBC configured · connection not tested")}
              </Badge>
            ) : (
              // Sem esta tag, a ausência do "Connected" era o único sinal — e
              // ninguém lê a falta de um selo como "nenhum agente vai rodar".
              <Badge className="shrink-0 gap-1 border-[#e0a115]/40 bg-[#fdf3e0] text-[#8a5a00] hover:bg-[#fdf3e0]">
                <AlertTriangle className="h-3 w-3" />
                {t("CBC connection required to run the agent")}
              </Badge>
            ))}
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center gap-2 px-4 py-8 text-[11px] text-[#7b8799]">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          {t("Loading the hierarchy…")}
        </div>
      ) : isError ? (
        <div
          role="alert"
          className="flex items-center justify-center gap-2 px-4 py-8 text-[12px] text-red-700"
        >
          <AlertTriangle className="h-3.5 w-3.5" />
          {t("Could not load the hierarchy")}
        </div>
      ) : displayRoots.length === 0 ? (
        <div className="px-4 py-6 text-[12px] text-[#7b8799]">
          {t("No data extracted")}
        </div>
      ) : management && selection ? (
        <>
          <div className={FILTER_ROW_CLASS}>
            <FilterSearch
              value={search}
              onChange={setSearch}
              onEnter={commitSearch}
              label={t("Search code or name…")}
            />
            <MultiSelectFilter
              options={unitTypeOptions}
              selectedKeys={selectedUnitTypes}
              onChange={setSelectedUnitTypes}
              allLabel="All unit types"
              searchPlaceholder="Search unit type…"
              emptyMessage="No unit type found."
              translateLabels
              triggerClassName={MANAGEMENT_SELECT_CLASS}
            />
            {countryOptions.length > 0 && (
              <MultiSelectFilter
                options={countryOptions}
                selectedKeys={selectedCountries}
                onChange={setSelectedCountries}
                allLabel="All countries/regions"
                searchPlaceholder="Search country…"
                emptyMessage="No country found."
                triggerClassName={MANAGEMENT_SELECT_CLASS}
              />
            )}
            <SourceSegment
              value={
                selectedOrigin === "sap" ? "erp" : (selectedOrigin ?? "all")
              }
              onChange={(value) =>
                setSelectedOrigin(
                  value === "all"
                    ? undefined
                    : value === "erp"
                      ? "sap"
                      : "manual",
                )
              }
            />
            {projectId && createUnitLevelOptions.length > 0 && (
              // No mapa a linha de filtros cabe inteira e o botão vai para o fim; na
              // lista ela quebra e o botão abre a linha de baixo, à esquerda.
              <Button
                className={cn(
                  "h-9 gap-1.5 bg-[var(--exed-navy)] px-3 text-[13px] font-medium text-white hover:bg-[var(--exed-navy)]/90",
                  view === "map" && "ml-auto",
                )}
                onClick={() =>
                  setUnitDialog({
                    mode: "create",
                    levelCode: createUnitLevelOptions[0].levelCode,
                  })
                }
              >
                <Plus aria-hidden="true" className="h-3.5 w-3.5" />
                {t("Create organizational unit")}
              </Button>
            )}
          </div>
          {/* No Gerenciamento a lista é paginada no servidor (`OrgManagementTab`); aqui só o Mapa.
              Árvore canônica: unidade sem descrição no ERP também se decide, como na lista. */}
          <OrgDecisionMap
            roots={canonicalRoots}
            matchKeys={mapMatchKeys}
            selection={selection}
            onRequestVerdict={requestVerdict}
            onReturn={onRequestManagementReturn}
          />
        </>
      ) : (
        <>
          {/* Sem a borda da Toolbar: quem fecha o bloco de filtros é a
              legenda logo abaixo — com a borda aqui, ela ficava colada na
              divisória e parecia riscada. */}
          <Toolbar className="border-b-0 pb-2">
            <ToolbarSearch
              placeholder={t("Search code or name…")}
              value={search}
              onChange={setSearch}
              onKeyDown={(event) => {
                if (event.key === "Enter") commitSearch();
              }}
            />
            {!executionOnly && (
              <MultiSelectFilter
                options={unitTypeOptions}
                selectedKeys={selectedUnitTypes}
                onChange={setSelectedUnitTypes}
                allLabel="All unit types"
                searchPlaceholder="Search unit type…"
                emptyMessage="No unit type found."
                translateLabels
              />
            )}
            {!executionOnly && countryOptions.length > 0 && (
              <MultiSelectFilter
                options={countryOptions}
                selectedKeys={selectedCountries}
                onChange={setSelectedCountries}
                allLabel="Country/Region"
                searchPlaceholder="Search country…"
                emptyMessage="No country found."
              />
            )}
            {!scopeVerdictLock && (
              <div
                role="group"
                aria-label={t("Verdict")}
                className="flex items-center gap-1"
              >
                {SCOPE_VERDICT_FILTER_OPTIONS.map((verdict) => {
                  const active = selectedScopeVerdict === verdict;
                  return (
                    <button
                      key={verdict}
                      type="button"
                      aria-pressed={active}
                      onClick={() =>
                        setSelectedScopeVerdict((current) =>
                          current === verdict ? undefined : verdict,
                        )
                      }
                      className={cn(
                        "h-8 rounded-md border px-2.5 text-xs transition-colors",
                        active
                          ? "border-[#08254c] bg-[#08254c] text-white"
                          : "border-[#e8ebf0] bg-white text-[#596b85] hover:border-[#cfd6e2]",
                      )}
                    >
                      {t(SCOPE_VERDICT_LABEL[verdict])}
                    </button>
                  );
                })}
              </div>
            )}
            {!executionOnly && (
              <div
                role="group"
                aria-label={t("Origin")}
                className="flex items-center gap-1"
              >
                {ORIGIN_FILTER_OPTIONS.map((option) => {
                  const active = selectedOrigin === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      aria-pressed={active}
                      onClick={() =>
                        setSelectedOrigin((current) =>
                          current === option.value ? undefined : option.value,
                        )
                      }
                      className={cn(
                        "h-8 rounded-md border px-2.5 text-xs transition-colors",
                        active
                          ? "border-[#08254c] bg-[#08254c] text-white"
                          : "border-[#e8ebf0] bg-white text-[#596b85] hover:border-[#cfd6e2]",
                      )}
                    >
                      {t(option.label)}
                    </button>
                  );
                })}
              </div>
            )}
            {!executionOnly && hasAwaitingDecision && (
              <div
                role="group"
                aria-label={t("Status")}
                className="flex items-center gap-1"
              >
                <button
                  type="button"
                  aria-pressed={awaitingDecisionOnly}
                  onClick={() => setAwaitingDecisionOnly((current) => !current)}
                  className={cn(
                    "h-8 rounded-md border px-2.5 text-xs transition-colors",
                    awaitingDecisionOnly
                      ? "border-[#08254c] bg-[#08254c] text-white"
                      : "border-[#e8ebf0] bg-white text-[#596b85] hover:border-[#cfd6e2]",
                  )}
                >
                  {t("Awaiting decision")}
                </button>
              </div>
            )}
            <ToolbarSpacer />
            {projectId &&
              !executionOnly &&
              createUnitLevelOptions.length > 0 && (
                // Fora de `RunAllControls` de propósito: aquele componente é
                // execução, e criar unidade manual (#845) é um eixo
                // independente — escopo. Um único botão aqui —
                // a escolha de nível mora DENTRO do `ManualUnitDialog`, não num
                // segundo controle na toolbar: um `<select>` nativo tem role
                // ARIA "combobox" por padrão, e a toolbar já tem um combobox
                // (o filtro "All unit types" via `MultiSelectFilter`) que os
                // testes existentes buscam por `getByRole("combobox")` sem
                // nome — um segundo combobox aqui tornava essa busca ambígua.
                // A árvore (Cards/List) também é uma hierarquia por pai/filho,
                // não uma lista agrupada por nível: não existe "a seção do
                // nível Plant" para pendurar um botão perto dela sem
                // reestruturar os dois componentes de visualização.
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setUnitDialog({
                      mode: "create",
                      levelCode: createUnitLevelOptions[0].levelCode,
                    })
                  }
                >
                  {t("Create unit")}
                </Button>
              )}
            {projectId && !hideExecution && (
              <RunAllControls
                runAllUnitCount={runAllUnitCount}
                cbcConnected={cbcConnected}
                runInProgressLabel={runInProgressLabel}
                runBlockedReason={runBlockedReason}
                onConfirmRunAll={runAllVisible}
                runQueue={runQueue}
                onStopQueue={onStopQueue}
              />
            )}
          </Toolbar>
          {!executionOnly && <OrgOriginLegend />}

          {lockedRoots.length === 0 ? (
            <div className="px-4 py-6 text-[12px] text-[#7b8799]">
              {t("No organizational unit matches the current filters.")}
            </div>
          ) : view === "list" ? (
            <OrgStructureListView
              roots={lockedRoots}
              onSelectNode={(node) =>
                onSelectNodeOverride
                  ? onSelectNodeOverride(node)
                  : setSelectedNodeKey(node.key)
              }
              onRunNode={handleRunNode}
              onRequestVerdict={requestVerdict}
              onEditUnit={(target) =>
                setUnitDialog({ mode: "edit", node: target })
              }
              onDeleteUnit={handleDeleteUnit}
              cbcConnected={cbcConnected}
              runInProgressLabel={runInProgressLabel}
              runBlockedReason={runBlockedReason}
              dispatchingUnitKeys={dispatchingUnitKeys}
              selection={selection}
              hideRun={hideExecution}
              hideVerdict={hideVerdictActions}
              hideCrud={executionOnly}
            />
          ) : (
            <OrgStructureCardsView
              roots={lockedRoots}
              onSelectNode={(node) =>
                onSelectNodeOverride
                  ? onSelectNodeOverride(node)
                  : setSelectedNodeKey(node.key)
              }
              onRunNode={handleRunNode}
              onRequestVerdict={requestVerdict}
              onEditUnit={(target) =>
                setUnitDialog({ mode: "edit", node: target })
              }
              onDeleteUnit={handleDeleteUnit}
              cbcConnected={cbcConnected}
              runInProgressLabel={runInProgressLabel}
              runBlockedReason={runBlockedReason}
              dispatchingUnitKeys={dispatchingUnitKeys}
              selection={selection}
              hideRun={hideExecution}
              hideVerdict={hideVerdictActions}
              hideCrud={executionOnly}
            />
          )}
        </>
      )}

      {overlays}
    </Card>
  );
}

/**
 * "Run all" da toolbar. Não há Pause: a conversa da unidade termina pelo Encerrar do
 * chat, e a fila pelo "Stop queue" da tela. A conexão com o CBC não se configura aqui:
 * é do projeto, e vive na tela de conexões (`TargetConnectionCard`).
 */
function RunAllControls({
  runAllUnitCount,
  cbcConnected,
  runInProgressLabel,
  runBlockedReason,
  onConfirmRunAll,
  runQueue,
  onStopQueue,
}: {
  runAllUnitCount: number;
  cbcConnected: boolean;
  runInProgressLabel?: string;
  runBlockedReason?: string | null;
  onConfirmRunAll: () => void;
  runQueue?: RunQueueState | null;
  onStopQueue?: () => void;
}) {
  const { t } = useTranslation();
  // Fila andando: o "Stop queue" ocupa o lugar do "Run all" e, à esquerda dele, a unidade
  // da vez. Fila parada: o aviso fica à esquerda e o "Run all" volta.
  if (runQueue?.kind === "running") {
    return (
      <>
        <span role="status" className="text-[13px] font-medium text-[#596b85]">
          {t("Unit {{current}} of {{total}}", {
            current: runQueue.current,
            total: runQueue.total,
          })}
        </span>
        <Button variant="outline" className="h-9" onClick={onStopQueue}>
          {t("Stop queue")}
        </Button>
      </>
    );
  }
  // Motivo na ordem em que a pessoa precisa resolver: sem conexão nada roda;
  // com conexão, falta ter o que rodar; com as duas, pode haver uma run viva.
  const runAllBlockedReason = !cbcConnected
    ? t("CBC connection required to run the agent")
    : runAllUnitCount === 0
      ? t("Approve at least one unit to run all.")
      : runInProgressLabel
        ? t(
            "A registration is already running for this project ({{label}}) — wait for it to finish.",
            { label: runInProgressLabel },
          )
        : (runBlockedReason ?? undefined);

  return (
    <>
      {runQueue?.kind === "stopped" && (
        <span role="status" className="text-[13px] font-medium text-[#596b85]">
          {t("Queue stopped at {{code}}", { code: runQueue.code })}
        </span>
      )}
      <RunAllButton
        label={t("Run all")}
        disabledReason={runAllBlockedReason ?? null}
        onClick={onConfirmRunAll}
      />
    </>
  );
}
